import { useEffect, useCallback, useState } from "react";
import { commonColumnProps } from "constants/TableConstants";
import RREPageContainer from "./RREPageContainer";
import { useDispatch, useSelector } from "utils/redux";
import { useParams } from "react-router-dom";
import {
  useDeleteRRETagMutation,
  useGetRRETagsQuery,
} from "services/rre-tag-api";
import Table from "components/Table/Table";
import type { TagType } from "types/DataTypes";
import type { CellClickArgs, Column } from "react-data-grid";
import CreateOrEditRRETagModal from "components/Modals/CreateOrEditRRETagModal";
import { setShowEditRRETagModal } from "reducers/rre";
import { setIsLoading, setValidationState } from "reducers/main";
import { useGetRreValidationsQuery } from "services/rre-api";

import "./RRETagsPage.scss";

const COLUMNS: Column<TagType>[] = [
  { key: "name", name: "Name", ...commonColumnProps },
  { key: "modified", name: "Modified", ...commonColumnProps },
  {
    key: "edit",
    name: "Edit",
    ...commonColumnProps,
    renderCell() {
      return (
        <div className="action-cell material-icons" data-icon="edit"></div>
      );
    },
  },
  {
    key: "delete",
    name: "Delete",
    ...commonColumnProps,
    renderCell() {
      return (
        <div className="action-cell material-icons" data-icon="delete"></div>
      );
    },
  },
];

const RRETagsPage = () => {
  const dispatch = useDispatch();
  const { rreId } = useParams<{ rreId: string }>();
  const [tagId, setTagId] = useState<number>(null);
  const showEditRRETagModal = useSelector(
    (state) => state.rre.showEditRRETagModal,
  );

  const { data: rreTags, isLoading } = useGetRRETagsQuery(
    {
      rreId,
    },
    { skip: !rreId },
  );

  useEffect(() => {
    dispatch(setIsLoading(isLoading));
  }, [dispatch, isLoading]);

  const [deleteRRETag, { isUninitialized, isSuccess, isError, reset, error }] =
    useDeleteRRETagMutation();

  const handleCellClick = useCallback(
    async (args: CellClickArgs<TagType, unknown>) => {
      setTagId(args.row.id);
      if (args.column.key === "edit") {
        dispatch(setShowEditRRETagModal(true));
      } else if (args.column.key === "delete") {
        if (isUninitialized) {
          deleteRRETag({
            rreId,
            tagId: `${args.row.id}`,
          });
        }
      }
    },
    [deleteRRETag, dispatch, isUninitialized, rreId],
  );

  if (isSuccess) {
    reset();
  }

  const { data: rreValidation } = useGetRreValidationsQuery(
    { id: rreId },
    { skip: !rreId },
  );

  useEffect(() => {
    if (rreValidation) {
      const validationData = Object.values(rreValidation).flat();
      const errors = validationData.filter((item) => item.level === "ERROR");
      const warnings = validationData.filter((item) => item.level === "WARN");
      dispatch(
        setValidationState({
          errors: errors,
          warnings: warnings,
        }),
      );
    }
  }, [dispatch, rreValidation]);

  return (
    <RREPageContainer>
      {showEditRRETagModal && tagId && (
        <CreateOrEditRRETagModal
          isOpen={showEditRRETagModal}
          onClose={() => dispatch(setShowEditRRETagModal(false))}
          isAddition={false}
          tagId={`${tagId}`}
        />
      )}
      {!isLoading && isError && <div>{JSON.stringify(error)}</div>}
      <Table
        columns={COLUMNS}
        rows={rreTags ? rreTags : []}
        rowKeyGetter={(row: TagType) => {
          return row.id;
        }}
        onCellClick={handleCellClick}
      />
    </RREPageContainer>
  );
};

export default RRETagsPage;
